import React from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgCopyTextTypography,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  OrganizationSummaryDto,
  StripeSubscriptionSummaryDto,
} from "@intelligems/ig-types";
import { Box, Grid } from "@mui/material";
import { useAdminCreateUsageBillingUrl } from "@src/api/shopify-app-subscriptions/admin-create-usage-billing-url";
import { useAdminPatchStripeSubscriptionLink } from "@src/api/stripe-subscription-links/admin-patch-stripe-subscription-link";
import { setDrawer } from "@src/store/slices/drawers";

interface SettingsSectionProps {
  subscriptionLink: StripeSubscriptionSummaryDto;
  orgSummary: OrganizationSummaryDto;
}

export const SettingsSection = ({
  subscriptionLink,
  orgSummary,
}: SettingsSectionProps) => {
  const [billingLink, setBillingLink] = useImmer<string>("");
  const usageBilling = useAdminCreateUsageBillingUrl();
  const [changes, setChanges] =
    useImmer<StripeSubscriptionSummaryDto>(subscriptionLink);

  const adminPatchSubscriptionLink = useAdminPatchStripeSubscriptionLink();
  const dispatch = useDispatch();

  const onInternalNotesChange = (value: string) => {
    setChanges((draft) => {
      draft.internalNotes = value;
      return draft;
    });
  };

  const onSave = () => {
    adminPatchSubscriptionLink.mutate({
      pathParams: {
        subscriptionLinkId: subscriptionLink.id,
      },
      body: {
        organizationId: subscriptionLink.organizationId,
        subscriptionLink: {
          autoScale: changes.autoScale,
          internalNotes: changes.internalNotes,
        },
      },
      queryParams: null,
    });
  };
  return (
    <Grid container item direction={"column"} rowSpacing={2}>
      <Grid item>
        <IgTextField
          disabled={adminPatchSubscriptionLink.isPending}
          value={changes.internalNotes || ""}
          multiline={true}
          fullWidth={true}
          onChange={(e) => onInternalNotesChange(e.target.value)}
          label="Internal Notes"
          title={"Internal Notes"}
        />
      </Grid>
      <Grid
        container
        item
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        {subscriptionLink.billingProvider === "shopify" ? (
          billingLink ? (
            <Grid item>
              <IgTypography sx={{ fontWeight: 600 }} variant={"body2"}>
                Billing Link:
              </IgTypography>{" "}
              <Box
                sx={{
                  maxWidth: "500px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <IgCopyTextTypography
                  text={billingLink}
                  toastMessage={() => toast.success("Billing Link Copied")}
                />
              </Box>
            </Grid>
          ) : (
            <Grid item>
              {subscriptionLink.billingProvider === "shopify" ? (
                <IgButton
                  loading={usageBilling.isPending}
                  variant={"text"}
                  onClick={() => {
                    usageBilling.mutate(
                      {
                        pathParams: undefined,
                        queryParams: undefined,
                        body: {
                          subscriptionLinkId: subscriptionLink.id,
                          orgId: subscriptionLink.organizationId,
                        },
                      },
                      {
                        onSuccess: (data) => {
                          setBillingLink(data.billingUrl || "");
                        },
                      }
                    );
                  }}
                >
                  Create Billing Link
                </IgButton>
              ) : null}
            </Grid>
          )
        ) : (
          <Grid item />
        )}

        <Grid item>
          <Grid
            container
            item
            columnSpacing={2}
            direction={"row"}
            justifyContent={"end"}
          >
            {["canceled", "cancelled", "incomplete_expired"].includes(
              subscriptionLink.status
            ) ? null : (
              <Grid item>
                <IgTooltip
                  title={orgSummary.agencies.length ? "Modify Agency" : ""}
                >
                  <span>
                    <IgButton
                      disabled={orgSummary.agencies.length > 0}
                      loading={adminPatchSubscriptionLink.isPending}
                      variant={"outlined"}
                      onClick={() =>
                        dispatch(
                          setDrawer({
                            type: "modifySubscription",
                            open: true,
                            usages: orgSummary.usages,
                            agencyId: "",
                            orgs: [
                              {
                                id: orgSummary.id,
                                shopId: orgSummary.shopId,
                              },
                            ],
                            stripeSubscriptionId:
                              subscriptionLink.stripeSubscriptionId,
                            subscriptionLinkIds: [subscriptionLink.id],
                          })
                        )
                      }
                    >
                      Modify Subscription
                    </IgButton>
                  </span>
                </IgTooltip>
              </Grid>
            )}
            <Grid item>
              <IgButton
                onClick={onSave}
                loading={adminPatchSubscriptionLink.isPending}
              >
                Save
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
